// Secure error handling utilities

// Generic error messages for different types of errors
const ERROR_MESSAGES = {
  AUTH: {
    INVALID_CREDENTIALS: "Email or password is incorrect.",
    UNAUTHORIZED:
      "You are not authorized to perform this action. Please contact your administrator.",
    SESSION_EXPIRED: "Your session has expired. Please log in again.",
    EMAIL_NOT_CONFIRMED: "Confirm your email before signing in.",
    TOO_MANY_REQUESTS: "Too many attempts. Wait a moment, then try again.",
    ACCOUNT_LOCKED:
      "Your account has been temporarily locked due to multiple failed attempts. Please try again later or contact support.",
    INVALID_EMAIL: "Please enter a valid email address.",
    WEAK_PASSWORD: "Password is too weak. Please choose a stronger password.",
    EMAIL_IN_USE:
      "An account with this email already exists. Please use a different email or try signing in.",
  },
  VALIDATION: {
    INVALID_INPUT: "Please check your input and try again",
    REQUIRED_FIELD: "This field is required",
    INVALID_FORMAT: "Invalid format",
  },
  NETWORK: {
    CONNECTION_ERROR:
      "Unable to connect to the server. Please check your internet connection and try again.",
    TIMEOUT: "Request timed out. Please try again.",
    OFFLINE: "You appear to be offline. Please check your internet connection.",
  },
  SERVER: {
    INTERNAL_ERROR: "An unexpected error occurred. Please try again later.",
    SERVICE_UNAVAILABLE:
      "Service temporarily unavailable. Please try again in a few minutes.",
    MAINTENANCE:
      "The service is currently under maintenance. Please try again later.",
  },
  CAPTCHA: {
    VERIFICATION_FAILED: "Captcha verification failed. Please try again.",
    EXPIRED: "Captcha has expired. Please complete the verification again.",
    MISSING: "Please complete the captcha verification to continue.",
  },
};

// Map Supabase error messages to our error types
const ERROR_CODE_MAPPING = {
  invalid_credentials: "INVALID_CREDENTIALS",
  email_not_confirmed: "EMAIL_NOT_CONFIRMED",
  over_request_rate_limit: "TOO_MANY_REQUESTS",
};

const ERROR_MAPPING = {
  "Invalid login credentials": "INVALID_CREDENTIALS",
  invalid_credentials: "INVALID_CREDENTIALS",
  "Email not confirmed": "EMAIL_NOT_CONFIRMED",
  "Too many requests": "TOO_MANY_REQUESTS",
  "User not found": "INVALID_CREDENTIALS",
  "Invalid email or password": "INVALID_CREDENTIALS",
  "Email or password is incorrect": "INVALID_CREDENTIALS",
  "Email rate limit exceeded": "TOO_MANY_REQUESTS",
  "Signup rate limit exceeded": "TOO_MANY_REQUESTS",
  "Account locked": "ACCOUNT_LOCKED",
  "Invalid email format": "INVALID_EMAIL",
  "Password too weak": "WEAK_PASSWORD",
  "Email already in use": "EMAIL_IN_USE",
  "Network error": "CONNECTION_ERROR",
  "Request timeout": "TIMEOUT",
  "Service unavailable": "SERVICE_UNAVAILABLE",
};

// Sanitize error messages for client display
export const sanitizeErrorMessage = (error) => {
  // If it's a known error type, return the generic message
  if (error?.type && ERROR_MESSAGES[error.type]?.[error.code]) {
    return ERROR_MESSAGES[error.type][error.code];
  }

  const errorCode = error?.code || error?.error_code;
  if (errorCode && ERROR_MESSAGES.AUTH[ERROR_CODE_MAPPING[errorCode]]) {
    return ERROR_MESSAGES.AUTH[ERROR_CODE_MAPPING[errorCode]];
  }

  // Check if we can map the error message to a known type
  const errorMessage = error?.message || error?.toString() || "";
  for (const [supabaseError, errorType] of Object.entries(ERROR_MAPPING)) {
    if (errorMessage.toLowerCase().includes(supabaseError.toLowerCase())) {
      return (
        ERROR_MESSAGES.AUTH[errorType] || ERROR_MESSAGES.SERVER.INTERNAL_ERROR
      );
    }
  }

  // For unknown errors, return a generic message
  return ERROR_MESSAGES.SERVER.INTERNAL_ERROR;
};

// Log errors securely (in production, this would send to a logging service)
export const logError = (error, context = {}) => {
  // In development, log the full error
  if (process.env.NODE_ENV === "development") {
    console.error("Error:", error);
    console.error("Context:", context);
  }

  // In production, log to your error tracking service
  // Example: Sentry.captureException(error, { extra: context });
};

// Handle API errors
export const handleApiError = (error) => {
  // Log the error
  logError(error);

  // Return a sanitized error message
  return {
    message: sanitizeErrorMessage(error),
    type: error?.type || "SERVER",
    code: error?.code || "INTERNAL_ERROR",
  };
};

// Get specific error message by type and code
export const getErrorMessage = (type, code) => {
  return ERROR_MESSAGES[type]?.[code] || ERROR_MESSAGES.SERVER.INTERNAL_ERROR;
};

// Check if error is retryable
export const isRetryableError = (error) => {
  const errorMessage = error?.message || error?.toString() || "";

  // Network errors are usually retryable
  if (
    errorMessage.toLowerCase().includes("network") ||
    errorMessage.toLowerCase().includes("timeout") ||
    errorMessage.toLowerCase().includes("connection")
  ) {
    return true;
  }

  // Rate limit errors can be retried after waiting
  if (
    errorMessage.toLowerCase().includes("rate limit") ||
    errorMessage.toLowerCase().includes("too many requests")
  ) {
    return true;
  }

  // Server errors might be temporary
  if (
    errorMessage.toLowerCase().includes("service unavailable") ||
    errorMessage.toLowerCase().includes("maintenance")
  ) {
    return true;
  }

  return false;
};
